import React, { useState } from "react";
import api from "@/lib/api";
import { PawPrint, Receipt, AlertTriangle, Clock, ChevronRight, X, Check } from "lucide-react";

/**
 * OnboardingWizard. 4 quick questions shown the first time a user opens a
 * case. Sets `case.preferences` which then drives which tabs appear in the
 * "More ▾" menu. Keeps the everyday user from being smacked in the face by
 * tabs that don't apply to them (e.g. Pets when they don't own any).
 */
const QUESTIONS = [
 {
 id: "has_pets",
 icon: PawPrint,
 title: "Do you have any pets at this rental?",
 sub: "We'll only show the Pets tools if you do.",
 options: [
 { value: true, label: "Yes, one or more pets" },
 { value: false, label: "No pets" },
 ],
 },
 {
 id: "tracks_rent",
 icon: Receipt,
 title: "Do you want to track your rent payments?",
 sub: "Some people record every payment to catch bond-time surprises. Others don't bother.",
 options: [
 { value: true, label: "Yes, keep a rent ledger" },
 { value: false, label: "No, skip rent tracking" },
 ],
 },
 {
 id: "in_dispute",
 icon: AlertTriangle,
 title: "Is there an active dispute right now?",
 sub: "If you're already in trouble with the landlord or agent, we'll surface the dispute tools first.",
 options: [
 { value: true, label: "Yes, something's going wrong" },
 { value: false, label: "No, just keeping records" },
 ],
 },
 {
 id: "tenancy_stage",
 icon: Clock,
 title: "Where are you in the tenancy?",
 sub: "Just so we can show the right reminders.",
 options: [
 { value: "just_starting", label: "Just moved in (last 30 days)" },
 { value: "mid_tenancy", label: "Settled in / mid-tenancy" },
 { value: "leaving_soon", label: "Planning to leave / lease almost up" },
 { value: "unsure", label: "Not sure" },
 ],
 },
];

export default function OnboardingWizard({ caseId, onDone, onSkip }) {
 const [step, setStep] = useState(0);
 const [answers, setAnswers] = useState({});
 const [saving, setSaving] = useState(false);

 const q = QUESTIONS[step];
 const Icon = q.icon;

 const pick = (value) => {
 const next = {...answers, [q.id]: value };
 setAnswers(next);
 if (step < QUESTIONS.length - 1) {
 setStep(s => s + 1);
 } else {
 finish(next);
 }
 };

 const finish = async (finalAnswers) => {
 setSaving(true);
 try {
 await api.put(`/cases/${caseId}/preferences`, {...finalAnswers, onboarded: true });
 onDone?.(finalAnswers);
 } catch (e) {
 alert(e.response?.data?.detail || "Couldn't save preferences");
 } finally {
 setSaving(false);
 }
 };

 const skip = async () => {
 setSaving(true);
 try {
 await api.put(`/cases/${caseId}/preferences`, { onboarded: true });
 onSkip?.();
 } catch (e) {
 // best effort
 } finally {
 setSaving(false);
 }
 };

 return (
 <div className="fixed inset-0 z-50 bg-[#0D2B52]/80 flex items-center justify-center p-4" data-testid="onboarding-wizard">
 <div className="bg-white max-w-lg w-full shadow-2xl">
 <div className="px-6 py-3 border-b border-[#E5E4DE] flex items-center justify-between">
 <div className="flex items-center gap-2">
 <span className="label-mono">A quick setup · {step + 1} of {QUESTIONS.length}</span>
 <ProgressDots active={step} total={QUESTIONS.length} />
 </div>
 <button onClick={skip} className="btn-ghost text-xs" data-testid="onboard-skip" title="Skip">
 <X size={14} strokeWidth={1.5} /> Skip
 </button>
 </div>

 <div className="px-6 py-7">
 <div className="flex items-start gap-3 mb-5">
 <div className="w-9 h-9 bg-[#E6F5F2] border border-[#1B6C62]/30 flex items-center justify-center flex-shrink-0">
 <Icon size={18} strokeWidth={1.75} className="text-[#1B6C62]" />
 </div>
 <div>
 <h2 className="font-serif text-xl text-[#0D2B52] leading-tight">{q.title}</h2>
 <p className="text-sm text-[#6B7280] mt-1">{q.sub}</p>
 </div>
 </div>

 <div className="space-y-2">
 {q.options.map((opt, i) => (
 <button
 key={i}
 onClick={() => pick(opt.value)}
 disabled={saving}
 className="w-full text-left px-4 py-3 border border-[#E5E4DE] hover:border-[#1B6C62] hover:bg-[#FAF9F6] flex items-center justify-between group transition-all"
 data-testid={`onboard-option-${q.id}-${String(opt.value)}`}
 >
 <span className="font-serif text-[#1A1A1A]">{opt.label}</span>
 <ChevronRight size={16} strokeWidth={1.5} className="text-[#6B7280] group-hover:text-[#1B6C62] group-hover:translate-x-0.5 transition-transform" />
 </button>
 ))}
 </div>
 </div>

 <div className="px-6 py-3 border-t border-[#E5E4DE] flex items-center justify-between">
 <button onClick={() => step > 0 && setStep(s => s - 1)} disabled={step === 0 || saving} className="btn-ghost text-xs disabled:opacity-30" data-testid="onboard-back">
 Back
 </button>
 <span className="text-[11px] text-[#6B7280] italic">You can change these later in the case settings.</span>
 </div>
 </div>
 </div>
 );
}

function ProgressDots({ active, total }) {
 return (
 <div className="flex gap-1.5">
 {Array.from({ length: total }, (_, i) => (
 <span key={i} className={`w-1.5 h-1.5 rounded-full transition-colors ${i <= active ? "bg-[#1B6C62]" : "bg-[#E5E4DE]"}`} />
 ))}
 </div>
 );
}
